import React, { useState, useEffect } from 'react';
import Spinner from '../components/ui/Spinner';
import Pagination from '../components/Pagination';
import VehicleCard from '../components/mobile/Vehicle';
import VehicleHeader from '../components/common/Vehicle';
import VehicleTable from '../components/desktop/Vehicle';
import VehicleForm from '../components/form/Vehicle';
import VehicleExpenses from './VehicleExpense';
import { getBranches } from '../services/branches';
import { createVehicle, getDimensions, getVehicles, getVehicleTypes, toggleVehicleStatus, updateVehiclePartial } from '../services/vehicles';
import { toast } from 'react-toastify';
import { USER_PROFILE, USER_ROLE } from '../services/auth';
import ConfirmationModal from '../components/modals/ConfirmationModal';

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [count, setCount] = useState(0);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleMode, setVehicleMode] = useState(null);
  const [initialData, setInitialData] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [showExpenses, setShowExpenses] = useState(false);
  const [expenseVehicle, setExpenseVehicle] = useState(false);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(0);
  const [vehicleModeFilter, setVehicleModeFilter] = useState('');
  const [dimensionFilter, setDimensionFilter] = useState(0);
  const [branchFilter, setBranchFilter] = useState(0);
  const [statusFilter, setStatusFilter] = useState('Active');
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmModalParams, setConfirmModalParams] = useState({
    open: false,
    title: 'Are you sure?',
    message: 'Do you really want to proceed with this action?',
    confirmText: 'Yes, Do it',
    cancelText: 'No, Cancel',
  });

  const role = localStorage.getItem(USER_ROLE);
  const showBranches = (role == 'admin');
  const vehiclesPerPage = 20;

  useEffect(() => {
    setPendingPage(1);
  }, [query, typeFilter, vehicleModeFilter, dimensionFilter, branchFilter, statusFilter]);

  useEffect(() => {
    if (pendingPage != null) {
      fetchVehicles(pendingPage);
    }
    if (dimensions.length == 0) {
      fetchDimesions();
    }
    if (vehicleTypes.length == 0) {
      fetchVehicleTypes();
    }
    const user = localStorage.getItem(USER_ROLE);
    if (user == 'admin' && branches.length == 0) {
      fetchBranches();
    }
  }, [pendingPage]);

  const fetchDimesions = async (page) => {
    setLoading(true);
    await getDimensions()
      .then(([err, data]) => {
        setDimensions(data.results);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const fetchVehicleTypes = async (page) => {
    setLoading(true);
    await getVehicleTypes()
      .then(([err, data]) => {
        setVehicleTypes(data);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const fetchBranches = async () => {
    setLoading(true);
    await getBranches({ paginate: false })
      .then(([err, data]) => {
        setBranches(data || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const fetchVehicles = async (page) => {
    setLoading(true);
    let branch = 0;
    if (showBranches) {
      branch = branchFilter;
    } else {
      const user = JSON.parse(localStorage.getItem(USER_PROFILE));
      branch = user.branch;
    }
    const params = { page, search,
      status: statusFilter.toLowerCase(),
      vehicle_type: typeFilter,
      vehicle_mode: vehicleModeFilter,
      dimension: dimensionFilter,
      branch: branch,
      pageSize:vehiclesPerPage
    };
    await getVehicles(params)
      .then(([err, data]) => {
        const rows = Array.isArray(data?.results) ? data.results : [];
        const normalizedFilter = (statusFilter || 'all').toLowerCase();
        const filteredRows = normalizedFilter === 'active'
          ? rows.filter((item) => Boolean(item?.is_active))
          : normalizedFilter === 'inactive'
          ? rows.filter((item) => !Boolean(item?.is_active))
          : rows;
        setVehicles(filteredRows);
        setCount(data.count);
        setCurrentPage(page);
        setPendingPage(null);
        setLoading(false);
      })
      .catch((error) => {
        setPendingPage(null);
        setLoading(false);
      });
  };

  const handleAdd = (vehicle_mode) => {
    setEditingVehicle(null);
    setVehicleMode(vehicle_mode);
    setShowForm(true);
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setVehicleMode(vehicle.vehicle_mode);
    setShowForm(true);
  };

  const handleSave = async (response) => {
    await fetchVehicles(currentPage);
    toast.success("Vehicle Saved Successfully.");
    setShowForm(false);
  };

  const handleCancel = async () => {
    setShowForm(false);
  }

  const onDownload = async (vehicle, type) => {
    const fileUrl = vehicle[`${type}_doc`];
    const filename = fileUrl.split('/').pop();
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const onSearch = (e) => {
    setSearch(e.target.value);
  };

  const onTypeFilter = (e) => {
    setTypeFilter(vehicleTypes[e.target.selectedIndex-1]?.id);
  };

  const onDimensionFilter = (e) => {
    setDimensionFilter(dimensions[e.target.selectedIndex-1]?.id);
  };

  const onBranchFilter = (e) => {
    setBranchFilter(branches[e.target.selectedIndex-1]?.id);
  };

  const onVehicleModeFilter = (e) => {
    setVehicleModeFilter(e.target.value || '');
  };

  const onStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleSearch = () => {
    setQuery(search.trim());
    setCurrentPage(1);
  };

  const handleViewExpenses = (vehicle) => {
    setExpenseVehicle(vehicle);
    setShowExpenses(true);
  };

  const onExpenseBack = () => {
    setExpenseVehicle(null);
    setShowExpenses(false);
  };

  const onClearSearch = () => {
    setSearch('');
    setQuery('');
    setCurrentPage(1);
    setPendingPage(1);
  };

  const handleToggleStatus = async (vehicle) => {
    setConfirmModalParams({
      ...confirmModalParams,
      open: true,
      title: vehicle.is_active ? 'Deactivate Vehicle' : 'Activate Vehicle',
      message: `Are you sure you want to ${vehicle.is_active ? 'deactivate' : 'activate'} the vehicle "${vehicle.vehicle_number}"?`,
      onConfirm: () => handleAction(vehicle),
    });
  };

  const handleAction = async (vehicle) => {
    setLoading(true);
    await toggleVehicleStatus(vehicle.id, !vehicle.is_active);
    setConfirmModalParams({ ...confirmModalParams, open: false });
    await fetchVehicles(currentPage);
    setLoading(false);
  };

  const handleVehicleSelection = (vehicle) => {
    setEditingVehicle(vehicle);
    setVehicleMode(vehicle.vehicle_mode);
    setShowForm(true);
  };

  return (
    <div className="p-4 w-full">
      <VehicleHeader
        dimensions={dimensions}
        vehicleTypes={vehicleTypes}
        branches={branches}
        search={search}
        count={count}
        typeFilter={typeFilter}
        vehicleModeFilter={vehicleModeFilter}
        dimensionFilter={dimensionFilter}
        branchFilter={branchFilter}
        onSearch={onSearch}
        handleSearch={handleSearch}
        onTypeFilter={onTypeFilter}
        onVehicleModeFilter={onVehicleModeFilter}
        onDimensionFilter={onDimensionFilter}
        onBranchFilter={onBranchFilter}
        statusFilter={statusFilter}
        onStatusFilter={onStatusFilter}
        handleAdd={handleAdd}
        onClearSearch={onClearSearch}
        onBack={handleCancel}
        vehicle={editingVehicle || expenseVehicle}
        formName={showForm ? "add_edit" : (showExpenses ? "expense" : null)}
      />

      {showForm ? (
        <VehicleForm
          vehicle={editingVehicle}
          vehicleMode={vehicleMode}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      ) : ((showExpenses ? (
        <VehicleExpenses
          vehicle={expenseVehicle}
          onBack={onExpenseBack}
        />
      ) : (
        <>
          {loading ? <Spinner /> : (
            <>
              <VehicleCard
                vehicles={vehicles}
                handleEdit={handleEdit}
                handleDownload={onDownload}
                onToggleStatus={handleToggleStatus}
              />

              <VehicleTable
                vehicles={vehicles}
                handleEdit={handleEdit}
                handleDownload={onDownload}
                onToggleStatus={handleToggleStatus}
                onViewExpenses={handleViewExpenses}
                onVehicleSelected={handleVehicleSelection}
              />
            </>
          )}
          

          {/* ✅ Pagination */}
          <div className="md:static fixed bottom-0 left-0 w-full bg-white border-t shadow z-10">
            {vehicles.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.max(1, Math.ceil(count / vehiclesPerPage))}
                onPageChange={setPendingPage}
              />
            )}
          </div>
        </>
      )))}
      <ConfirmationModal
        open={confirmModalParams.open}
        title={confirmModalParams.title}
        message={confirmModalParams.message}
        onConfirm={confirmModalParams.onConfirm}
        onCancel={() => setConfirmModalParams({ ...confirmModalParams, open: false })}
        confirmText={confirmModalParams.confirmText}
        cancelText={confirmModalParams.cancelText}
      />
    </div>
  );
};

export default Vehicles;
